import {
  Parent_Type_Enum,
  Risk_Assessment_Result_Control_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import type { DefaultValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CustomisableForm } from 'src/components/form/form/CustomisableForm';
import type { FormContextProps } from 'src/components/form/form/types';
import { useScoringSettings } from 'src/ratings/useScoringSettings';

import type { ImpactCategory } from '@/blocks/risk-scoring-settings/types';
import type { ConfigType } from '@/hooks/queries/risk-assessment-result-config/types';

import type { AssessmentTypeEnum } from '../types';
import { getParentTypeFromControlType } from './getParentTypeFromControlType';
import MultiImpactRiskAssessmentResultFormFields from './MultiImpactRiskAssessmentResultFormFields';
import RiskAssessmentResultFormFields from './RiskAssessmentResultFormFields';
import type {
  MultiImpactRiskAssessmentResultFormDataFields,
  RiskAssessmentResultFormDataFields,
} from './riskAssessmentResultSchema';
import {
  MultiImpactRiskAssessmentResultSchema,
  RiskAssessmentResultSchema,
} from './riskAssessmentResultSchema';

type Props = Omit<
  FormContextProps<RiskAssessmentResultFormDataFields>,
  'formId' | 'i18n' | 'parentType' | 'schema'
> & {
  beforeFieldsSlot?: ReactNode;
  showSelector?: AssessmentTypeEnum;
  assessmentMode: AssessmentTypeEnum;
  configType: ConfigType;
  // Config version the viewed rating was assessed under, so its fields resolve
  // against that version (read-only stale ratings).
  configId?: string | null;
  disableRiskSelector: boolean;
  values?:
    | RiskAssessmentResultFormDataFields
    | MultiImpactRiskAssessmentResultFormDataFields;
};

const POSSIBLE_PARENT_TYPES = [
  Parent_Type_Enum.ControlledRiskAssessmentResult,
  Parent_Type_Enum.UncontrolledRiskAssessmentResult,
  Parent_Type_Enum.RiskUncontrolledSecondLineResult,
  Parent_Type_Enum.RiskControlledSecondLineResult,
  Parent_Type_Enum.RiskControlledInternalAuditResult,
  Parent_Type_Enum.RiskUncontrolledInternalAuditResult,
];

const toMultiImpactDefaults = (
  defaults: DefaultValues<RiskAssessmentResultFormDataFields>,
  categories: ImpactCategory[]
): DefaultValues<MultiImpactRiskAssessmentResultFormDataFields> => {
  const { Impact: _impact, ...rest } = defaults;

  return {
    ...rest,
    ImpactRatings: categories.map((c) => ({
      categoryName: c.name,
      rating: null,
    })),
  };
};

const RiskAssessmentResultForm: FC<Props> = (props) => {
  const { t } = useTranslation();
  const [controlType, setControlType] =
    useState<Risk_Assessment_Result_Control_Type_Enum>(
      Risk_Assessment_Result_Control_Type_Enum.Uncontrolled
    );

  const { getImpactCategories } = useScoringSettings({
    configType: props.configType,
    configIds: props.configId ? [props.configId] : undefined,
  });
  // Decide single vs multi from the config the rating was assessed under, not
  // the org's latest config, so a historic single-impact rating keeps its L/I
  // form after the org moves to multi-impact. New ratings have no configId, so
  // this falls back to latest.
  const impactCategories = getImpactCategories(props.configId);
  const hasMultipleImpacts = impactCategories.length >= 2;

  const parentType = getParentTypeFromControlType(
    controlType,
    props.assessmentMode
  );

  const multiImpactDefaults = useMemo(
    () =>
      hasMultipleImpacts && props.defaultValues
        ? toMultiImpactDefaults(props.defaultValues, impactCategories)
        : undefined,
    [hasMultipleImpacts, props.defaultValues, impactCategories]
  );

  const multiImpactValues =
    props.values && 'ImpactRatings' in props.values ? props.values : undefined;

  const fieldProps = {
    readOnly: props.readOnly,
    onControlTypeChange: setControlType,
    disableRiskSelector: props.disableRiskSelector,
    showSelector: props.showSelector,
    assessmentMode: props.assessmentMode,
    configType: props.configType,
    configId: props.configId,
  };

  if (hasMultipleImpacts) {
    // Props are typed for RiskAssessmentResultFormDataFields but the two form
    // data types share the same shape for all non-generic props (onSave accepts
    // both via a union in ConnectedRiskAssessmentResultForm, renderTemplate only
    // uses layout props). A targeted refactor isn't possible without changing
    // FormContextProps which deeply couples renderTemplate to the form data type.
    return (
      <CustomisableForm<MultiImpactRiskAssessmentResultFormDataFields>
        {...(props as unknown as Omit<
          FormContextProps<MultiImpactRiskAssessmentResultFormDataFields>,
          'formId' | 'i18n' | 'parentType' | 'schema'
        >)}
        defaultValues={multiImpactDefaults ?? {}}
        values={multiImpactValues}
        schema={MultiImpactRiskAssessmentResultSchema}
        i18n={t('assessmentResults')}
        formId={'assessment-result-form'}
        possibleParentTypes={POSSIBLE_PARENT_TYPES}
        parentType={parentType}
      >
        {props.beforeFieldsSlot}
        <MultiImpactRiskAssessmentResultFormFields {...fieldProps} />
      </CustomisableForm>
    );
  }

  return (
    <CustomisableForm
      {...props}
      schema={RiskAssessmentResultSchema}
      i18n={t('assessmentResults')}
      formId={'assessment-result-form'}
      renderTemplate={props.renderTemplate}
      possibleParentTypes={POSSIBLE_PARENT_TYPES}
      parentType={parentType}
    >
      {props.beforeFieldsSlot}
      <RiskAssessmentResultFormFields {...fieldProps} />
    </CustomisableForm>
  );
};

export default RiskAssessmentResultForm;
